import { Formio } from "formiojs";

const FieldComponent =
  Formio.Components.components.field;

/* =========================================================
 * Allgemeine Hilfsfunktionen
 * ========================================================= */

function getByPath(object, path) {
  if (!path) {
    return object;
  }

  return String(path)
    .split(".")
    .filter(Boolean)
    .reduce((current, key) => {
      if (
        current === null ||
        current === undefined
      ) {
        return undefined;
      }

      return current[key];
    }, object);
}

function resolveTemplate(template, data) {
  return String(template || "").replace(
    /\{([^}]+)\}/g,
    (match, key) => {
      const value = getByPath(
        data,
        key.trim()
      );

      if (
        value === null ||
        value === undefined
      ) {
        return "";
      }

      return encodeURIComponent(
        String(value)
      );
    }
  );
}

function deepClone(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return value;
  }

  return JSON.parse(
    JSON.stringify(value)
  );
}

/**
 * Ergänzt ausschließlich fehlende Konfigurationswerte.
 * Bereits im Form.io-Builder gespeicherte Werte haben immer Vorrang.
 * Arrays werden nur gesetzt, wenn die Eigenschaft vollständig fehlt;
 * vorhandene Arrays werden niemals automatisch zusammengeführt.
 */
function isPlainObject(value) {
    return (
        value !== null &&
        typeof value === "object" &&
        !Array.isArray(value)
    );
}

function mergeMissingDefaults(target, defaults) {
    if (!isPlainObject(target) || !isPlainObject(defaults)) {
        return target;
    }

    Object.keys(defaults).forEach((key) => {
        const defaultValue = defaults[key];

        if (target[key] === undefined) {
            target[key] = deepClone(defaultValue);
            return;
        }

        if (
            isPlainObject(target[key]) &&
            isPlainObject(defaultValue)
        ) {
            mergeMissingDefaults(target[key], defaultValue);
        }
    });

    return target;
}


function normalizeList(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return [];
  }

  let sourceValues;

  if (Array.isArray(value)) {
    sourceValues = value;
  } else if (typeof value === "string") {
    sourceValues = value.split(",");
  } else {
    sourceValues = [value];
  }

  return sourceValues
    .map((entry) => {
      if (
        entry &&
        typeof entry === "object"
      ) {
        if (
          entry.value !== null &&
          entry.value !== undefined
        ) {
          return String(entry.value).trim();
        }

        if (
          entry.label !== null &&
          entry.label !== undefined
        ) {
          return String(entry.label).trim();
        }
      }

      return String(entry).trim();
    })
    .filter(Boolean);
}

function normalizeDependsOn(config) {
  if (
    typeof config.dependsOnText === "string" &&
    config.dependsOnText.trim() !== ""
  ) {
    return normalizeList(
      config.dependsOnText
    );
  }

  return normalizeList(
    config.dependsOn
  );
}

function normalizeQueryParams(config) {
  return Array.isArray(
    config && config.queryParams
  )
    ? config.queryParams
    : [];
}

function hasValue(value) {
  return (
    value !== null &&
    value !== undefined &&
    value !== ""
  );
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function normalizeAxiosError(error) {
  const responseData =
    error &&
    error.response &&
    error.response.data
      ? error.response.data
      : null;

  if (responseData) {
    if (responseData.messageDe) {
      return responseData.messageDe;
    }

    if (responseData.Message) {
      return responseData.Message;
    }

    if (responseData.message) {
      return responseData.message;
    }

    if (typeof responseData === "string") {
      return responseData;
    }
  }

  return (
    error?.message ||
    "Die Informationen konnten nicht geladen werden."
  );
}

function valuesMatch(
  currentValue,
  expectedValue,
  ignoreCase = true
) {
  if (
    currentValue === null ||
    currentValue === undefined
  ) {
    return false;
  }

  if (
    typeof expectedValue === "boolean"
  ) {
    if (typeof currentValue === "boolean") {
      return currentValue === expectedValue;
    }

    const normalizedCurrent =
      String(currentValue)
        .trim()
        .toLowerCase();

    if (
      normalizedCurrent === "true" ||
      normalizedCurrent === "false"
    ) {
      return (
        normalizedCurrent === "true"
      ) === expectedValue;
    }

    return false;
  }

  if (
    typeof expectedValue === "number"
  ) {
    return Number(currentValue) === expectedValue;
  }

  const left =
    String(currentValue);

  const right =
    String(expectedValue ?? "");

  if (ignoreCase !== false) {
    return (
      left.toLowerCase() ===
      right.toLowerCase()
    );
  }

  return left === right;
}

function parseConfiguredValue(value) {
  if (typeof value !== "string") {
    return value;
  }

  const trimmed =
    value.trim();

  if (trimmed === "true") {
    return true;
  }

  if (trimmed === "false") {
    return false;
  }

  if (
    trimmed !== "" &&
    !Number.isNaN(Number(trimmed))
  ) {
    return Number(trimmed);
  }

  return trimmed;
}

/* =========================================================
 * Generische Query-Parameter
 *
 * Quellen:
 * - form       -> Form.io-Gesamtdaten
 * - community  -> window.communityData
 * - currentUser-> window.currentUser
 * - window     -> window
 * - literal    -> definition.value
 *
 * Beispiel:
 * {
 *   name: "communityIdentifier",
 *   source: "community",
 *   path: "communityIdentifier",
 *   required: true
 * }
 * ========================================================= */

function getQueryParamValue(
  definition,
  formData
) {
  if (
    !definition ||
    typeof definition !== "object"
  ) {
    return undefined;
  }

  const source =
    String(
      definition.source || "form"
    )
      .trim()
      .toLowerCase();

  const path =
    String(
      definition.path || ""
    ).trim();

  switch (source) {
    case "literal":
      return definition.value;

    case "window":
      return path
        ? getByPath(window, path)
        : undefined;

    case "currentuser":
    case "user":
      return path
        ? getByPath(
            window.currentUser || {},
            path
          )
        : undefined;

    case "community":
      return path
        ? getByPath(
            window.communityData || {},
            path
          )
        : undefined;

    case "form":
    default:
      return path
        ? getByPath(
            formData || {},
            path
          )
        : undefined;
  }
}

function appendQueryParams(
  endpoint,
  definitions,
  formData,
  options = {}
) {
  let result =
    String(endpoint || "");

  const params =
    Array.isArray(definitions)
      ? definitions
      : [];

  const throwOnMissingRequired =
    options.throwOnMissingRequired !== false;

  params.forEach((definition) => {
    if (
      !definition ||
      typeof definition !== "object"
    ) {
      return;
    }

    const name =
      String(
        definition.name || ""
      ).trim();

    if (!name) {
      return;
    }

    const value =
      getQueryParamValue(
        definition,
        formData
      );

    if (!hasValue(value)) {
      if (
        definition.required === true &&
        throwOnMissingRequired
      ) {
        throw new Error(
          `Query-Parameter '${name}' konnte nicht aufgelöst werden.`
        );
      }

      return;
    }

    result +=
      (result.includes("?") ? "&" : "?") +
      encodeURIComponent(name) +
      "=" +
      encodeURIComponent(
        String(value)
      );
  });

  return result;
}

/* =========================================================
 * ApiInfoPanelComponent
 *
 * Zweck:
 * - lädt genau EIN Objekt von einer API
 * - zeigt dessen Eigenschaften als Info-/Statusansicht
 * - unterstützt generische Query-Parameter aus mehreren Kontexten
 * - keine CRUD-, Tabellen-, Such- oder Pagination-Logik
 * ========================================================= */



const API_INFO_PANEL_CONFIG_VERSION = 2;

function getApiInfoPanelConfigDefaults() {
  return {
    api: {
      loadEndpoint: "",
      loadMethod: "GET",
      loadBody: null,
      resultPath: "",
      dependsOnText: "",
      dependsOn: [],
      queryParams: [],
      autoLoad: true
    },

    status: {
      show: false,
      field: "",
      values: []
    },

    fields: [],
    displayMode: "list",

    loadingMessage: "Informationen werden geladen …",
    emptyMessage: "Keine Informationen vorhanden.",
    loadErrorMessage: "Die Informationen konnten nicht geladen werden.",

    hideEmptyFields: false,
    showReloadButton: false,
    reloadButtonLabel: "Neu laden"
  };
}

export default class ApiInfoPanelComponent
  extends FieldComponent {

  static schema(...extend) {
    return FieldComponent.schema(
      {
        type: "apiInfoPanel",
        label: "API Info Panel",
        key: "apiInfoPanel",

        input: true,
        persistent: false,
        tableView: false,

        api: {
          loadEndpoint: "",
          loadMethod: "GET",
          loadBody: null,
          resultPath: "",

          dependsOnText: "",
          dependsOn: [],

          /*
           * Generische Query-Parameter.
           *
           * Beispiel:
           * [
           *   {
           *     name: "communityIdentifier",
           *     source: "community",
           *     path: "communityIdentifier",
           *     required: true
           *   }
           * ]
           */
          queryParams: [],

          autoLoad: true
        },

        status: {
          show: false,
          field: "",
          values: []
        },

        fields: [],

        displayMode: "list",

        loadingMessage:
          "Informationen werden geladen …",

        emptyMessage:
          "Keine Informationen vorhanden.",

        loadErrorMessage:
          "Die Informationen konnten nicht geladen werden.",

        hideEmptyFields: false,

        showReloadButton: false,
        reloadButtonLabel: "Neu laden"
      },
      ...extend
    );
  }

  static get builderInfo() {
    return {
      title: "API Info Panel",
      icon: "info-circle",
      group: "basic",
      weight: 26,
      schema:
        ApiInfoPanelComponent.schema()
    };
  }

  constructor(
    component,
    options,
    data
  ) {
    super(
      component,
      options,
      data
    );

    this._apiLoading = false;
    this._apiError = null;
    this._apiLoadedSignature = null;

    this._infoData = null;
    this._loadingPromise = null;

    this._rootChangeHandler = null;
    this._dependencyChangeTimer = null;
  }

  init() {
    this.upgradeComponentConfig();
    super.init();
  }

  /*
   * Migriert ältere ApiInfoPanel-Konfigurationen, ohne bestehende
   * Builder-Einstellungen zu überschreiben.
   */
  upgradeComponentConfig() {
    const component = this.component || {};

    if (!isPlainObject(component.api)) {
      component.api = {};
    }

    if (!isPlainObject(component.status)) {
      component.status = {};
    }

    let version = Number(
      component.apiInfoPanelConfigVersion || 0
    );

    // v0 -> v1: Query-Parameter-Konfiguration eingeführt.
    if (version < 1) {
      if (component.api.queryParams === undefined) {
        component.api.queryParams = [];
      }

      version = 1;
    }

    // Fehlende aktuelle Defaults bei jedem Laden ergänzen. Bereits
    // gespeicherte Werte und Arrays bleiben vollständig erhalten.
    mergeMissingDefaults(
      component,
      getApiInfoPanelConfigDefaults()
    );

    if (version < 2) {
      version = 2;
    }

    component.apiInfoPanelConfigVersion =
      Math.max(version, API_INFO_PANEL_CONFIG_VERSION);
  }

  get apiConfig() {
    return this.component.api || {};
  }

  get isBuilderMode() {
    return !!(
      window.__OZG_FORMIO_BUILDER_MODE === true ||
      (this.options && this.options.builder === true) ||
      (
        this.root &&
        this.root.options &&
        this.root.options.builder === true
      )
    );
  }

  getFormData() {
    if (
      this.root &&
      this.root.data &&
      typeof this.root.data === "object"
    ) {
      return this.root.data;
    }

    if (
      this.root &&
      this.root.submission &&
      this.root.submission.data &&
      typeof this.root.submission.data === "object"
    ) {
      return this.root.submission.data;
    }

    return this.data || {};
  }

  getResolvedEndpoint(
    throwOnMissingRequired = true
  ) {
    const config =
      this.apiConfig;

    const formData =
      this.getFormData();

    const baseEndpoint =
      resolveTemplate(
        config.loadEndpoint,
        formData
      );

    return appendQueryParams(
      baseEndpoint,
      normalizeQueryParams(config),
      formData,
      {
        throwOnMissingRequired
      }
    );
  }

  get requestSignature() {
    const config =
      this.apiConfig;

    const formData =
      this.getFormData();

    const dependsOn =
      normalizeDependsOn(config);

    let endpoint;

    try {
      endpoint =
        this.getResolvedEndpoint(false);
    } catch (error) {
      endpoint =
        resolveTemplate(
          config.loadEndpoint,
          formData
        );
    }

    const queryParamState =
      normalizeQueryParams(config)
        .map((definition) => {
          if (!definition) {
            return null;
          }

          return {
            name:
              definition.name || "",
            source:
              definition.source || "form",
            path:
              definition.path || "",
            required:
              definition.required === true,
            value:
              getQueryParamValue(
                definition,
                formData
              )
          };
        })
        .filter(Boolean);

    return JSON.stringify({
      endpoint,

      method: String(
        config.loadMethod || "GET"
      ).toUpperCase(),

      dependencies:
        dependsOn.map((key) => {
          return {
            key,
            value: getByPath(
              formData,
              key
            )
          };
        }),

      queryParams:
        queryParamState
    });
  }

  shouldLoad() {
    if (this.isBuilderMode) {
      return false;
    }

    const config =
      this.apiConfig;

    if (
      config.autoLoad === false ||
      !config.loadEndpoint
    ) {
      return false;
    }

    const formData =
      this.getFormData();

    const dependsOn =
      normalizeDependsOn(config);

    const dependenciesReady =
      dependsOn.every((key) => {
        return hasValue(
          getByPath(
            formData,
            key
          )
        );
      });

    if (!dependenciesReady) {
      return false;
    }

    const requiredQueryParamsReady =
      normalizeQueryParams(config)
        .filter((definition) => {
          return (
            definition &&
            definition.required === true
          );
        })
        .every((definition) => {
          return hasValue(
            getQueryParamValue(
              definition,
              formData
            )
          );
        });

    return requiredQueryParamsReady;
  }

  getAxios() {
    const axios =
      window.GLOBALAXIOS;

    if (!axios) {
      throw new Error(
        "GLOBALAXIOS ist nicht verfügbar."
      );
    }

    return axios;
  }

  getApiBaseUrl() {
    return (
      window.GLOBALCONFIG?.API_URL ||
      ""
    );
  }

  async executeRequest(
    method,
    endpoint,
    payload
  ) {
    if (this.isBuilderMode) {
      console.debug(
        "[ApiInfoPanel] API-Aufruf im Builder blockiert:",
        endpoint
      );

      return {
        status: 200,
        data: {}
      };
    }

    const axios =
      this.getAxios();

    const url =
      this.getApiBaseUrl() +
      endpoint;

    const normalizedMethod =
      String(
        method || "GET"
      ).toUpperCase();

    switch (normalizedMethod) {
      case "POST":
        return axios.post(
          url,
          payload || {}
        );

      case "GET":
      default:
        return axios.get(url);
    }
  }

  getFields() {
    return Array.isArray(
      this.component.fields
    )
      ? this.component.fields
      : [];
  }

  getStatusConfig() {
    return (
      this.component.status &&
      typeof this.component.status === "object"
    )
      ? this.component.status
      : {};
  }

  getValueMapping(
    configuredValues,
    currentValue
  ) {
    const mappings =
      Array.isArray(configuredValues)
        ? configuredValues
        : [];

    return mappings.find((mapping) => {
      if (!mapping) {
        return false;
      }

      return valuesMatch(
        currentValue,
        parseConfiguredValue(
          mapping.value
        ),
        mapping.ignoreCase !== false
      );
    });
  }

  formatDate(
    value,
    withTime
  ) {
    if (!hasValue(value)) {
      return "";
    }

    try {
      const date =
        new Date(value);

      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
        return String(value);
      }

      if (withTime) {
        return date.toLocaleString(
          "de-DE"
        );
      }

      return date.toLocaleDateString(
        "de-DE"
      );
    } catch (error) {
      return String(value);
    }
  }

  formatNumber(
    value,
    options = {}
  ) {
    if (!hasValue(value)) {
      return "";
    }

    const number =
      Number(value);

    if (Number.isNaN(number)) {
      return String(value);
    }

    try {
      return new Intl.NumberFormat(
        "de-DE",
        options
      ).format(number);
    } catch (error) {
      return String(value);
    }
  }

  formatFieldValue(
    field,
    rawValue
  ) {
    const mapping =
      this.getValueMapping(
        field.valueMap,
        rawValue
      );

    if (mapping) {
      return hasValue(mapping.label)
        ? String(mapping.label)
        : "";
    }

    const format =
      String(
        field.format || "text"
      ).toLowerCase();

    switch (format) {
      case "datetime":
        return this.formatDate(
          rawValue,
          true
        );

      case "date":
        return this.formatDate(
          rawValue,
          false
        );

      case "boolean":
        if (
          rawValue === true ||
          String(rawValue).toLowerCase() === "true"
        ) {
          return (
            field.trueLabel ||
            "Ja"
          );
        }

        if (
          rawValue === false ||
          String(rawValue).toLowerCase() === "false"
        ) {
          return (
            field.falseLabel ||
            "Nein"
          );
        }

        return "";

      case "number":
        return this.formatNumber(
          rawValue
        );

      case "currency":
        return this.formatNumber(
          rawValue,
          {
            style: "currency",
            currency:
              field.currency ||
              "EUR"
          }
        );

      case "percent":
        return this.formatNumber(
          rawValue,
          {
            style: "percent",
            maximumFractionDigits: 2
          }
        );

      case "text":
      default:
        return hasValue(rawValue)
          ? String(rawValue)
          : "";
    }
  }

  renderStatus() {
    const statusConfig =
      this.getStatusConfig();

    if (
      statusConfig.show !== true ||
      !statusConfig.field ||
      !this._infoData
    ) {
      return "";
    }

    const rawValue =
      getByPath(
        this._infoData,
        statusConfig.field
      );

    const mapping =
      this.getValueMapping(
        statusConfig.values,
        rawValue
      );

    if (!mapping) {
      if (!hasValue(rawValue)) {
        return "";
      }

      return `
        <div class="api-info-panel-status mb-3">
          <span
            class="badge badge-secondary"
            style="
              font-size: 1rem;
              padding: 0.5rem 0.75rem;
              font-weight: 600;
            "
          >
            ${escapeHtml(rawValue)}
          </span>
        </div>
      `;
    }

    const label =
      hasValue(mapping.label)
        ? mapping.label
        : rawValue;

    const variant =
      String(
        mapping.variant ||
        "secondary"
      ).trim();

    return `
      <div class="api-info-panel-status mb-3">
        <span
          class="badge badge-${escapeHtml(variant)}"
          style="
            font-size: 1rem;
            padding: 0.5rem 0.75rem;
            font-weight: 600;
          "
        >
          ${escapeHtml(label)}
        </span>
      </div>
    `;
  }

  renderFields() {
    if (!this._infoData) {
      return "";
    }

    const fields =
      this.getFields();

    const rows =
      fields
        .map((field) => {
          if (
            !field ||
            !field.field
          ) {
            return "";
          }

          const rawValue =
            getByPath(
              this._infoData,
              field.field
            );

          const displayValue =
            this.formatFieldValue(
              field,
              rawValue
            );

          const hideEmpty =
            field.hideWhenEmpty === true ||
            this.component.hideEmptyFields === true;

          if (
            hideEmpty &&
            !hasValue(displayValue)
          ) {
            return "";
          }

          const emptyLabel =
            hasValue(field.emptyLabel)
              ? field.emptyLabel
              : "";

          const finalDisplay =
            hasValue(displayValue)
              ? displayValue
              : emptyLabel;

          return `
            <div
              class="api-info-panel-row d-flex flex-wrap py-2 border-bottom"
            >
              <div
                class="api-info-panel-label font-weight-bold pr-3"
                style="min-width: 190px;"
              >
                ${escapeHtml(
                  field.label ||
                  field.field
                )}
              </div>

              <div
                class="api-info-panel-value flex-grow-1"
              >
                ${escapeHtml(finalDisplay)}
              </div>
            </div>
          `;
        })
        .filter(Boolean);

    return rows.join("");
  }

  renderReloadButton() {
    if (
      this.component.showReloadButton !== true
    ) {
      return "";
    }

    return `
      <div class="mt-3">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          ref="reloadButton"
        >
          ${escapeHtml(
            this.component.reloadButtonLabel ||
            "Neu laden"
          )}
        </button>
      </div>
    `;
  }

  render() {
    const status = [];

    if (this._apiLoading) {
      status.push(`
        <div class="alert alert-info">
          ${escapeHtml(
            this.component.loadingMessage ||
            "Informationen werden geladen …"
          )}
        </div>
      `);
    }

    if (this._apiError) {
      status.push(`
        <div class="alert alert-danger">
          ${escapeHtml(
            this._apiError ||
            this.component.loadErrorMessage ||
            "Die Informationen konnten nicht geladen werden."
          )}
        </div>
      `);
    }

    let body = "";

    if (
      !this._apiLoading &&
      !this._apiError
    ) {
      if (!this._infoData) {
        body = `
          <div class="text-muted">
            ${escapeHtml(
              this.component.emptyMessage ||
              "Keine Informationen vorhanden."
            )}
          </div>
        `;
      } else {
        body = `
          ${this.renderStatus()}

          <div class="api-info-panel-fields">
            ${this.renderFields()}
          </div>

          ${this.renderReloadButton()}
        `;
      }
    }

    return super.render(`
      <div
        class="api-info-panel"
        ref="infoRoot"
      >
        ${status.join("")}
        ${body}
      </div>
    `);
  }

  attach(element) {
    const attached =
      super.attach(element);

    this.loadRefs(
      element,
      {
        infoRoot: "single",
        reloadButton: "single"
      }
    );

    if (this.refs.reloadButton) {
      this.addEventListener(
        this.refs.reloadButton,
        "click",
        () => {
          this.reloadApiInfo();
        }
      );
    }

    if (
      !this.isBuilderMode &&
      !this._rootChangeHandler &&
      this.root &&
      typeof this.root.on === "function"
    ) {
      this._rootChangeHandler =
        () => {
          if (
            this._dependencyChangeTimer
          ) {
            clearTimeout(
              this._dependencyChangeTimer
            );
          }

          this._dependencyChangeTimer =
            setTimeout(() => {
              const signature =
                this.requestSignature;

              if (
                signature !==
                this._apiLoadedSignature
              ) {
                this.loadApiInfo(
                  false
                );
              }
            }, 100);
        };

      this.root.on(
        "change",
        this._rootChangeHandler
      );
    }

    window.setTimeout(() => {
      if (!this.isBuilderMode) {
        this.loadApiInfo(false);
      }
    }, 0);

    return attached;
  }

  async loadApiInfo(
    force = false
  ) {
    if (
      this._apiLoading ||
      !this.shouldLoad()
    ) {
      return;
    }

    const signature =
      this.requestSignature;

    if (
      force !== true &&
      this._apiLoadedSignature ===
      signature
    ) {
      return;
    }

    if (this._loadingPromise) {
      return this._loadingPromise;
    }

    const config =
      this.apiConfig;

    let endpoint;

    try {
      endpoint =
        this.getResolvedEndpoint(true);
    } catch (error) {
      this._apiLoadedSignature =
        signature;

      this._infoData = null;
      this._apiError =
        normalizeAxiosError(error);

      this.redraw();
      return;
    }

    const method =
      String(
        config.loadMethod || "GET"
      ).toUpperCase();

    this._apiLoading = true;
    this._apiError = null;

    this.redraw();

    this._loadingPromise =
      (async () => {
        try {
          const response =
            await this.executeRequest(
              method,
              endpoint,
              config.loadBody || {}
            );

          const result =
            getByPath(
              response.data,
              config.resultPath
            );

          if (
            result &&
            typeof result === "object" &&
            !Array.isArray(result)
          ) {
            this._infoData =
              deepClone(result);
          } else {
            this._infoData = null;
          }

          this.dataValue =
            deepClone(
              this._infoData
            );

          this._apiLoadedSignature =
            signature;

        } catch (error) {
          this._apiLoadedSignature =
            signature;

          this._infoData = null;

          this._apiError =
            normalizeAxiosError(error);

          console.warn(
            "[ApiInfoPanel] Laden fehlgeschlagen",
            {
              key:
                this.component.key,
              endpoint,
              error
            }
          );

        } finally {
          this._apiLoading = false;
          this._loadingPromise = null;

          this.redraw();
        }
      })();

    return this._loadingPromise;
  }

  reloadApiInfo() {
    this._apiLoadedSignature = null;

    return this.loadApiInfo(true);
  }

  setValue(
    value,
    flags = {}
  ) {
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      this._infoData =
        deepClone(value);

      this.dataValue =
        deepClone(value);
    }

    return super.setValue(
      value,
      flags
    );
  }

  getValue() {
    return deepClone(
      this._infoData
    );
  }

  destroy() {
    if (
      this._dependencyChangeTimer
    ) {
      clearTimeout(
        this._dependencyChangeTimer
      );
    }

    this._dependencyChangeTimer =
      null;

    if (
      this._rootChangeHandler &&
      this.root &&
      typeof this.root.off === "function"
    ) {
      this.root.off(
        "change",
        this._rootChangeHandler
      );
    }

    this._rootChangeHandler = null;
    this._loadingPromise = null;

    return super.destroy();
  }
}

/* =========================================================
 * Konfiguration im Form.io-Editor
 * ========================================================= */

ApiInfoPanelComponent.editForm =
  function () {
    return {
      components: [
        {
          type: "tabs",
          key: "tabs",
          components: [
            {
              key: "display",
              label: "Anzeige",
              components: [
                {
                  type: "textfield",
                  key: "label",
                  label: "Label"
                },
                {
                  type: "textfield",
                  key: "key",
                  label: "API-Key",
                  validate: {
                    required: true
                  }
                },
                {
                  type: "checkbox",
                  key: "persistent",
                  label:
                    "In Formulardaten speichern",
                  defaultValue: false
                },
                {
                  type: "checkbox",
                  key: "hideEmptyFields",
                  label:
                    "Leere Felder ausblenden",
                  defaultValue: false
                },
                {
                  type: "checkbox",
                  key: "showReloadButton",
                  label:
                    "Neuladen-Button anzeigen",
                  defaultValue: false
                },
                {
                  type: "textfield",
                  key: "reloadButtonLabel",
                  label:
                    "Beschriftung Neuladen-Button",
                  defaultValue:
                    "Neu laden"
                }
              ]
            },
            {
              key: "api",
              label: "API",
              components: [
                {
                  type: "textfield",
                  key: "api.loadEndpoint",
                  label:
                    "Load Endpoint",
                  placeholder:
                    "/api/status",
                  validate: {
                    required: true
                  }
                },
                {
                  type: "select",
                  key: "api.loadMethod",
                  label:
                    "Load Method",
                  data: {
                    values: [
                      {
                        label: "GET",
                        value: "GET"
                      },
                      {
                        label: "POST",
                        value: "POST"
                      }
                    ]
                  },
                  defaultValue: "GET"
                },
                {
                  type: "textfield",
                  key: "api.resultPath",
                  label:
                    "Result-Pfad",
                  placeholder:
                    "data"
                },
                {
                  type: "textfield",
                  key: "api.dependsOnText",
                  label:
                    "Abhängige Formularfelder",
                  placeholder:
                    "selectedUserId, scopeType",
                  description:
                    "Kommagetrennte Liste. Der API-Aufruf erfolgt erst, wenn alle Werte vorhanden sind."
                },
                {
                  type: "datagrid",
                  key: "api.queryParams",
                  label:
                    "Query-Parameter",
                  reorder: true,
                  description:
                    "Optionale Query-Parameter aus Formular-, Gemeinde-, Benutzer- oder globalem Window-Kontext.",
                  components: [
                    {
                      type: "textfield",
                      key: "name",
                      label:
                        "Parametername",
                      placeholder:
                        "communityIdentifier",
                      validate: {
                        required: true
                      }
                    },
                    {
                      type: "select",
                      key: "source",
                      label:
                        "Quelle",
                      data: {
                        values: [
                          {
                            label:
                              "Formulardaten",
                            value: "form"
                          },
                          {
                            label:
                              "Gemeindekontext",
                            value:
                              "community"
                          },
                          {
                            label:
                              "Benutzerkontext",
                            value:
                              "currentUser"
                          },
                          {
                            label:
                              "Window / global",
                            value: "window"
                          },
                          {
                            label:
                              "Fester Wert",
                            value: "literal"
                          }
                        ]
                      },
                      defaultValue:
                        "form"
                    },
                    {
                      type: "textfield",
                      key: "path",
                      label:
                        "Pfad",
                      placeholder:
                        "communityIdentifier",
                      description:
                        "Beispiel Gemeinde: communityIdentifier; Benutzer: userId; Window: someGlobal.value. Bei 'Fester Wert' leer lassen."
                    },
                    {
                      type: "textfield",
                      key: "value",
                      label:
                        "Fester Wert",
                      placeholder:
                        "active",
                      conditional: {
                        show: true,
                        when: "source",
                        eq: "literal"
                      }
                    },
                    {
                      type: "checkbox",
                      key: "required",
                      label:
                        "Pflichtparameter",
                      defaultValue: false,
                      description:
                        "Wenn aktiv, wird der API-Aufruf erst ausgeführt, sobald dieser Wert verfügbar ist."
                    }
                  ]
                },
                {
                  type: "checkbox",
                  key: "api.autoLoad",
                  label:
                    "Automatisch laden",
                  defaultValue: true
                }
              ]
            },
            {
              key: "status",
              label: "Status",
              components: [
                {
                  type: "checkbox",
                  key: "status.show",
                  label:
                    "Status-Badge anzeigen",
                  defaultValue: false
                },
                {
                  type: "textfield",
                  key: "status.field",
                  label:
                    "Status DTO-Feld",
                  placeholder:
                    "isConnected"
                },
                {
                  type: "datagrid",
                  key: "status.values",
                  label:
                    "Statuswerte",
                  reorder: true,
                  components: [
                    {
                      type: "textfield",
                      key: "value",
                      label:
                        "Technischer Wert",
                      placeholder:
                        "true"
                    },
                    {
                      type: "textfield",
                      key: "label",
                      label:
                        "Anzeigetext",
                      placeholder:
                        "Verbunden"
                    },
                    {
                      type: "select",
                      key: "variant",
                      label:
                        "Darstellung",
                      data: {
                        values: [
                          {
                            label: "Success",
                            value: "success"
                          },
                          {
                            label: "Primary",
                            value: "primary"
                          },
                          {
                            label: "Secondary",
                            value: "secondary"
                          },
                          {
                            label: "Info",
                            value: "info"
                          },
                          {
                            label: "Warning",
                            value: "warning"
                          },
                          {
                            label: "Danger",
                            value: "danger"
                          },
                          {
                            label: "Dark",
                            value: "dark"
                          }
                        ]
                      },
                      defaultValue:
                        "secondary"
                    },
                    {
                      type: "checkbox",
                      key: "ignoreCase",
                      label:
                        "Groß-/Kleinschreibung ignorieren",
                      defaultValue: true
                    }
                  ]
                }
              ]
            },
            {
              key: "fields",
              label: "Felder",
              components: [
                {
                  type: "datagrid",
                  key: "fields",
                  label:
                    "Anzeigefelder",
                  reorder: true,
                  components: [
                    {
                      type: "textfield",
                      key: "label",
                      label:
                        "Beschriftung",
                      validate: {
                        required: true
                      }
                    },
                    {
                      type: "textfield",
                      key: "field",
                      label:
                        "DTO-Feld",
                      validate: {
                        required: true
                      }
                    },
                    {
                      type: "select",
                      key: "format",
                      label:
                        "Format",
                      data: {
                        values: [
                          {
                            label: "Text",
                            value: "text"
                          },
                          {
                            label:
                              "Datum und Uhrzeit",
                            value:
                              "datetime"
                          },
                          {
                            label: "Datum",
                            value: "date"
                          },
                          {
                            label:
                              "Ja / Nein",
                            value:
                              "boolean"
                          },
                          {
                            label: "Zahl",
                            value: "number"
                          },
                          {
                            label: "Währung",
                            value:
                              "currency"
                          },
                          {
                            label: "Prozent",
                            value:
                              "percent"
                          }
                        ]
                      },
                      defaultValue:
                        "text"
                    },
                    {
                      type: "textfield",
                      key: "currency",
                      label:
                        "Währung",
                      placeholder:
                        "EUR",
                      conditional: {
                        show: true,
                        when: "format",
                        eq: "currency"
                      }
                    },
                    {
                      type: "textfield",
                      key: "trueLabel",
                      label:
                        "Text für Ja/true",
                      defaultValue:
                        "Ja"
                    },
                    {
                      type: "textfield",
                      key: "falseLabel",
                      label:
                        "Text für Nein/false",
                      defaultValue:
                        "Nein"
                    },
                    {
                      type: "textfield",
                      key: "emptyLabel",
                      label:
                        "Text bei leerem Wert",
                      placeholder:
                        "-"
                    },
                    {
                      type: "checkbox",
                      key: "hideWhenEmpty",
                      label:
                        "Feld ausblenden, wenn leer",
                      defaultValue:
                        false
                    },
                    {
                      type: "datagrid",
                      key: "valueMap",
                      label:
                        "Wertzuordnungen",
                      reorder: true,
                      components: [
                        {
                          type: "textfield",
                          key: "value",
                          label:
                            "Technischer Wert"
                        },
                        {
                          type: "textfield",
                          key: "label",
                          label:
                            "Anzeigetext"
                        },
                        {
                          type: "checkbox",
                          key: "ignoreCase",
                          label:
                            "Groß-/Kleinschreibung ignorieren",
                          defaultValue:
                            true
                        }
                      ]
                    }
                  ]
                }
              ]
            },
            {
              key: "messages",
              label: "Texte",
              components: [
                {
                  type: "textfield",
                  key: "loadingMessage",
                  label:
                    "Ladetext",
                  defaultValue:
                    "Informationen werden geladen …"
                },
                {
                  type: "textfield",
                  key: "emptyMessage",
                  label:
                    "Leerzustand",
                  defaultValue:
                    "Keine Informationen vorhanden."
                },
                {
                  type: "textfield",
                  key: "loadErrorMessage",
                  label:
                    "Fehlertext",
                  defaultValue:
                    "Die Informationen konnten nicht geladen werden."
                }
              ]
            }
          ]
        }
      ]
    };
  };

Formio.Components.addComponent(
  "apiInfoPanel",
  ApiInfoPanelComponent
);
